/**
 * Congé maternité et congé de paternité du travailleur indépendant.
 *
 * DEUX PRESTATIONS QUI N'OBÉISSENT PAS À LA MÊME LOGIQUE, et c'est là que se
 * logent les mauvaises surprises :
 *
 *   - L'ALLOCATION FORFAITAIRE DE REPOS MATERNEL est liée à la NAISSANCE. Elle
 *     vaut le plafond mensuel de la sécurité sociale, se verse en deux moitiés,
 *     et ne dépend ni de la durée de l'arrêt ni du chiffre d'affaires.
 *   - LES INDEMNITÉS JOURNALIÈRES sont liées à l'INTERRUPTION RÉELLE. Elles
 *     exigent de cesser toute activité pendant au moins huit semaines, dont six
 *     après l'accouchement. En dessous, ce n'est pas une part des indemnités qui
 *     se perd : c'est la totalité.
 *
 * SIX MOIS D'AFFILIATION, PAS DOUZE. La condition des prestations maternité et
 * paternité est de six mois à la date présumée de l'accouchement. Les douze mois
 * continus que tout le monde cite sont ceux des indemnités journalières d'ARRÊT
 * MALADIE, une autre prestation. Confondre les deux fait renoncer des femmes qui
 * y ont droit.
 *
 * LE CONGÉ DE PATERNITÉ NE COMPORTE AUCUNE ALLOCATION FORFAITAIRE. Il repose sur
 * les seules indemnités journalières, vingt-cinq jours au plus, à prendre dans
 * les six mois qui suivent la naissance.
 *
 * TOUS LES CHIFFRES VIENNENT DU RÉFÉRENTIEL ARBITRÉ, complété le 8 septembre
 * 2026 sur deux sources officielles concordantes : ameli.fr, mis à jour le
 * 1er juin 2026, et service-public.gouv.fr, vérifié le 25 juin 2026. Ils se
 * recalculent depuis le plafond annuel de la sécurité sociale, ce qui est le
 * meilleur garde-fou contre un barème périmé : 48 060 ÷ 12 = 4 005 pour
 * l'allocation, 48 060 ÷ 730 = 65,84 pour l'indemnité journalière. Une valeur
 * qui ne tomberait plus juste signalerait un plafond changé.
 *
 * UN SUJET NE SE CHIFFRE PAS, et le référentiel le range parmi les interdits :
 * la durée minimale et le fractionnement du congé de paternité d'un
 * indépendant, sur lesquels les sources officielles divergent. Le congé
 * supplémentaire de naissance, lui, se chiffre depuis le 25 septembre 2026 :
 * Service-Public (F39686) et ameli.fr en donnent la date et le barème, en brut
 * pour l'une et en net pour l'autre, et les deux concordent.
 */

import { dateEnToutesLettres, nettoyer } from "./documentText.js";

/** D'où viennent les chiffres, et quand ils ont été relevés. */
export const BAREME = {
    annee: "2026",
    releveLe: "2026-09-08",
    source: "ameli.fr mis à jour le 1er juin 2026, service-public.gouv.fr vérifié le 25 juin 2026",
};

/** Le plafond annuel de la sécurité sociale, dont tout le reste se déduit. */
export const PASS = 48060;

/** L'allocation forfaitaire de repos maternel : le plafond MENSUEL. */
export const ALLOCATION_REPOS = Math.round((PASS / 12) * 100) / 100;
/** Son montant réduit, exactement un dixième. */
export const ALLOCATION_REPOS_REDUITE = Math.round(ALLOCATION_REPOS * 10) / 100;

/** L'indemnité journalière forfaitaire : le plafond annuel divisé par 730. */
export const IJ_FORFAITAIRE = Math.round((PASS / 730) * 100) / 100;
/** Son montant réduit, au millième parce que le dixième de 65,84 en a un. */
export const IJ_FORFAITAIRE_REDUITE = Math.round(IJ_FORFAITAIRE * 100) / 1000;

/**
 * Le revenu annuel moyen en dessous duquel tout passe au dixième.
 *
 * Dix pour cent de la moyenne des plafonds des trois années considérées. C'est
 * le même seuil que celui de l'arrêt maladie, et il n'est donc pas recalculé
 * ici : le référentiel le fixe à 4 582 €.
 */
export const REVENU_PLANCHER = 4582;

/** L'affiliation exigée, en mois. SIX, pas les douze de l'arrêt maladie. */
export const AFFILIATION_MOIS = 6;

/** La cessation d'activité sans laquelle aucune indemnité n'est due. */
export const ARRET_MINIMAL_JOURS = 56;
export const ARRET_MINIMAL_APRES_JOURS = 42;

/** Le report du prénatal sur le postnatal, sur prescription médicale. */
export const REPORT_PRENATAL_MAX_JOURS = 21;

/**
 * Les durées du congé maternité, en jours, selon la situation familiale.
 *
 * Le total est toujours la somme du prénatal et du postnatal : c'est ce que
 * vérifie le harnais, parce qu'une durée recopiée de travers ne se voit pas.
 */
export const MATERNITE = {
    premier: { prenatal: 42, postnatal: 70, total: 112 },
    troisieme: { prenatal: 56, postnatal: 126, total: 182 },
    jumeaux: { prenatal: 84, postnatal: 154, total: 238 },
    triples: { prenatal: 168, postnatal: 154, total: 322 },
};

/**
 * Le congé supplémentaire de naissance, ouvert depuis le 1er juillet 2026.
 *
 * CHAQUE PARENT Y A DROIT, EN PLUS DU CONGÉ DE MATERNITÉ OU DE PATERNITÉ : un ou
 * deux mois, en une fois ou en deux périodes d'un mois, à condition de
 * suspendre son activité et d'avoir six mois d'affiliation au premier jour de
 * ce congé. Il vise les enfants nés ou adoptés depuis le 1er janvier 2026, et
 * se prend dans les neuf mois qui suivent la naissance ; pour une naissance du
 * premier semestre 2026, les neuf mois courent du 1er juillet, soit jusqu'au
 * 31 mars 2027 (Service-Public F39686, relue le 25 septembre 2026).
 *
 * L'INDEMNITÉ EST DÉGRESSIVE : l'indemnité forfaitaire de 65,84 € × 0,7 le
 * premier mois, × 0,6 le second, soit 46,09 € puis 39,50 € bruts par jour ;
 * ameli.fr écrit 43 € et 36,85 € nets, CSG et CRDS de 6,7 % retirées. Sous le
 * plancher de revenu, 6,58 € bruts par jour les deux mois, sans dégressivité.
 */
export const CONGE_SUPPLEMENTAIRE = {
    ouvertLe: "2026-07-01",
    enfantsDepuis: "2026-01-01",
    coefficients: [0.7, 0.6],
    delaiMois: 9,
    affiliationMois: 6,
    moisMaximum: 2,
};

/** Les indemnités par jour du premier et du second mois, au centime. */
export const IJ_NAISSANCE = CONGE_SUPPLEMENTAIRE.coefficients.map((c) => Math.round(IJ_FORFAITAIRE * c * 100) / 100);

/** Sous le plancher de revenu : un dixième de 1/730 du plafond, les deux mois. */
export const IJ_NAISSANCE_REDUITE = Math.round((PASS / 730) * 0.1 * 100) / 100;

/** Le congé de paternité, en jours calendaires. */
export const PATERNITE_JOURS = { simple: 25, multiple: 32 };
/** Le délai pour le prendre, en mois à compter de la naissance. */
export const PATERNITE_DELAI_MOIS = 6;

/* --------------------------------------------------------------------- */
/* Les dates                                                              */
/* --------------------------------------------------------------------- */

/** Une date ISO découpée, ou null si elle n'en est pas une. */
function jour(iso) {
    const t = nettoyer(iso);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return null;
    const [a, m, j] = t.split("-").map(Number);
    const d = new Date(Date.UTC(a, m - 1, j));
    if (d.getUTCFullYear() !== a || d.getUTCMonth() !== m - 1 || d.getUTCDate() !== j) return null;
    return d;
}

/** Une date ISO décalée de n jours. */
export function ajouterJours(iso, n) {
    const d = jour(iso);
    if (!d) return null;
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
}

/**
 * Une date ISO décalée de n mois, en reculant sur le quantième.
 *
 * Le 31 août plus six mois donne le 28 février, pas le 3 mars : un délai en
 * mois se compte de quantième à quantième, et le dernier jour du mois quand
 * celui d'arrivée est plus court.
 */
export function ajouterMois(iso, n) {
    const d = jour(iso);
    if (!d) return null;
    const quantieme = d.getUTCDate();
    d.setUTCDate(1);
    d.setUTCMonth(d.getUTCMonth() + n);
    const dernier = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
    d.setUTCDate(Math.min(quantieme, dernier));
    return d.toISOString().slice(0, 10);
}

/** Le nombre de jours entre deux dates ISO, négatif si la seconde précède. */
export function joursEntre(debut, fin) {
    const a = jour(debut);
    const b = jour(fin);
    if (!a || !b) return null;
    return Math.round((b - a) / 86400000);
}

/* --------------------------------------------------------------------- */
/* Les montants                                                           */
/* --------------------------------------------------------------------- */

/** Un montant écrit à la française, insécables ordinaires comprises. */
export function euros(valeur) {
    const chiffres = valeur.toLocaleString("fr-FR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    return `${chiffres.replace(/ /g, " ")} €`;
}

/** Un revenu annuel moyen, accepté avec virgule ou point. */
export function revenu(brut) {
    const t = nettoyer(String(brut ?? ""))
        .replace(/\s/g, "")
        .replace(",", ".");
    if (!t) return { valeur: 0, motif: "vide" };
    if (!/^\d{1,9}(\.\d{1,2})?$/.test(t)) return { valeur: 0, motif: "format" };
    return { valeur: Math.round(Number(t) * 100) / 100, motif: null };
}

/** Un nombre de jours d'arrêt. */
export function jours(brut) {
    const t = nettoyer(String(brut ?? "")).replace(/\s/g, "");
    if (!t) return { valeur: 0, motif: "vide" };
    if (!/^\d{1,3}$/.test(t)) return { valeur: 0, motif: "format" };
    const n = Number(t);
    if (n <= 0) return { valeur: n, motif: "nul" };
    return { valeur: n, motif: null };
}

/** Le revenu passe-t-il sous le plancher ? */
export function auPlancher(reponses) {
    const r = reponses || {};
    if (r.revenuConnu === "sous") return true;
    const montant = revenu(r.revenuMoyen);
    if (montant.motif) return false;
    return montant.valeur < REVENU_PLANCHER;
}

/* --------------------------------------------------------------------- */
/* Le calendrier de la maternité                                          */
/* --------------------------------------------------------------------- */

/**
 * Les dates du congé, à partir de la date présumée de l'accouchement.
 *
 * Le prénatal s'arrête la veille de l'accouchement et le postnatal commence le
 * jour même : c'est ainsi que le total tombe juste, seize semaines et non seize
 * semaines et un jour.
 */
export function calendrierMaternite(reponses) {
    const r = reponses || {};
    const duree = MATERNITE[r.situation];
    if (!duree || !jour(r.dateAccouchement)) return null;

    const reporte = Math.min(
        Math.max(0, jours(r.reportPrenatal).valeur || 0),
        REPORT_PRENATAL_MAX_JOURS,
        // On ne reporte jamais au point de passer sous la part prénatale
        // obligatoire : quatorze jours doivent précéder l'accouchement.
        duree.prenatal - (ARRET_MINIMAL_JOURS - ARRET_MINIMAL_APRES_JOURS),
    );

    const prenatal = duree.prenatal - reporte;
    const postnatal = duree.postnatal + reporte;

    return {
        prenatal,
        postnatal,
        reporte,
        total: duree.total,
        debut: ajouterJours(r.dateAccouchement, -prenatal),
        accouchement: nettoyer(r.dateAccouchement),
        fin: ajouterJours(r.dateAccouchement, postnatal - 1),
        // La période que la loi impose, et sans laquelle rien n'est versé.
        obligatoireDebut: ajouterJours(
            r.dateAccouchement,
            -(ARRET_MINIMAL_JOURS - ARRET_MINIMAL_APRES_JOURS),
        ),
        obligatoireFin: ajouterJours(r.dateAccouchement, ARRET_MINIMAL_APRES_JOURS - 1),
    };
}

/**
 * Ce que la maternité rapporte, et ce qui se perd si l'arrêt est trop court.
 *
 * L'allocation reste acquise quoi qu'il arrive : elle accompagne la naissance.
 * Les indemnités journalières, elles, tombent entièrement sous huit semaines.
 */
export function droitsMaternite(reponses) {
    const r = reponses || {};
    const calendrier = calendrierMaternite(r);
    if (!calendrier) return null;

    const arretVoulu = jours(r.joursArret);
    if (arretVoulu.motif) return null;

    const reduit = auPlancher(r);
    const allocation = reduit ? ALLOCATION_REPOS_REDUITE : ALLOCATION_REPOS;
    const journaliere = reduit ? IJ_FORFAITAIRE_REDUITE : IJ_FORFAITAIRE;

    // On n'indemnise jamais au-delà de la durée du congé.
    const indemnises = Math.min(arretVoulu.valeur, calendrier.total);
    const assezLong = arretVoulu.valeur >= ARRET_MINIMAL_JOURS;
    const ij = assezLong ? Math.round(journaliere * indemnises * 100) / 100 : 0;

    return {
        reduit,
        allocation,
        // Deux versements : la moitié au début du congé, la moitié à la fin de
        // la période obligatoire de huit semaines.
        versement: Math.round((allocation / 2) * 100) / 100,
        journaliere,
        joursIndemnises: assezLong ? indemnises : 0,
        joursArret: arretVoulu.valeur,
        assezLong,
        ij,
        total: Math.round((allocation + ij) * 100) / 100,
        // Ce que l'arrêt trop court laisse sur la table.
        perdu: assezLong
            ? 0
            : Math.round(journaliere * Math.min(calendrier.total, ARRET_MINIMAL_JOURS) * 100) / 100,
        calendrier,
    };
}

/* --------------------------------------------------------------------- */
/* Le congé de paternité                                                  */
/* --------------------------------------------------------------------- */

/** Les dates du congé de paternité : sa durée, et la date limite pour le poser. */
export function calendrierPaternite(reponses) {
    const r = reponses || {};
    if (!jour(r.dateNaissance)) return null;
    const plafond = r.naissances === "multiple" ? PATERNITE_JOURS.multiple : PATERNITE_JOURS.simple;
    return {
        plafond,
        naissance: nettoyer(r.dateNaissance),
        limite: ajouterMois(r.dateNaissance, PATERNITE_DELAI_MOIS),
    };
}

/** Ce que le congé de paternité rapporte. Aucune allocation, que des indemnités. */
export function droitsPaternite(reponses) {
    const r = reponses || {};
    const calendrier = calendrierPaternite(r);
    if (!calendrier) return null;

    const pris = jours(r.joursArret);
    if (pris.motif) return null;

    const reduit = auPlancher(r);
    const journaliere = reduit ? IJ_FORFAITAIRE_REDUITE : IJ_FORFAITAIRE;
    const indemnises = Math.min(pris.valeur, calendrier.plafond);

    return {
        reduit,
        journaliere,
        joursIndemnises: indemnises,
        joursArret: pris.valeur,
        // Aucune allocation forfaitaire : c'est la différence avec la maternité.
        allocation: 0,
        ij: Math.round(journaliere * indemnises * 100) / 100,
        total: Math.round(journaliere * indemnises * 100) / 100,
        calendrier,
    };
}

/**
 * Le congé supplémentaire de naissance qui s'ouvre après le congé calculé :
 * s'il est dû, ce qu'il rapporte par jour, et jusqu'à quand le prendre.
 *
 * Rend `null` tant que la date de naissance manque, et `{ ouvert: false }`
 * pour un enfant né avant 2026.
 */
export function congeSupplementaire(reponses) {
    const r = reponses || {};
    const date = r.parent === "mere" ? r.dateAccouchement : r.dateNaissance;
    if (!jour(date)) return null;
    const naissance = nettoyer(date);
    if (naissance < CONGE_SUPPLEMENTAIRE.enfantsDepuis) return { ouvert: false, naissance };

    const reduit = auPlancher(r);
    const parJour = reduit ? [IJ_NAISSANCE_REDUITE, IJ_NAISSANCE_REDUITE] : IJ_NAISSANCE;
    // Les neuf mois courent de la naissance, ou du 1er juillet 2026 pour les
    // enfants nés avant l'ouverture du congé ; le dernier jour est la veille
    // du même quantième.
    const depart = naissance < CONGE_SUPPLEMENTAIRE.ouvertLe ? CONGE_SUPPLEMENTAIRE.ouvertLe : naissance;
    return {
        ouvert: true,
        reduit,
        parJour,
        // Pour se figurer un mois : trente jours, le mois réel se comptant de
        // date à date (du 15 juillet au 14 août, par exemple).
        moisDeTrenteJours: parJour.map((v) => Math.round(v * 30 * 100) / 100),
        limite: ajouterJours(ajouterMois(depart, CONGE_SUPPLEMENTAIRE.delaiMois), -1),
        naissance,
    };
}

/* --------------------------------------------------------------------- */
/* Le tri et les refus                                                    */
/* --------------------------------------------------------------------- */

/**
 * Le premier refus, et le seul qui ferme tout : l'affiliation.
 *
 * Six mois à la date présumée de l'accouchement, ou au début du congé de
 * paternité. Sans eux, ni allocation ni indemnités.
 */
export function concerne(reponses) {
    const r = reponses || {};
    if (!r.parent) return { concerne: null, motif: null };
    if (!r.affiliation) return { concerne: null, motif: null };
    if (r.affiliation === "non") return { concerne: false, motif: "affiliationTropCourte" };
    return { concerne: true, motif: null };
}

/** Ce qui manque, ce qui est refusé, et ce qui mérite un mot. */
export function valider(reponses) {
    const r = reponses || {};
    const manques = [];
    const invalides = [];
    const alertes = [];
    const mere = r.parent === "mere";

    if (!concerne(r).concerne) {
        return { valide: false, manques: ["parent"], invalides, alertes };
    }

    const dateAttendue = mere ? "dateAccouchement" : "dateNaissance";
    if (!nettoyer(r[dateAttendue])) manques.push(dateAttendue);
    else if (!jour(r[dateAttendue])) invalides.push({ champ: dateAttendue, motif: "format" });

    if (mere && !r.situation) manques.push("situation");
    if (!mere && !r.naissances) manques.push("naissances");

    if (!r.revenuConnu) manques.push("revenuConnu");
    else if (r.revenuConnu === "chiffre") {
        const montant = revenu(r.revenuMoyen);
        if (montant.motif === "vide") manques.push("revenuMoyen");
        else if (montant.motif) invalides.push({ champ: "revenuMoyen", motif: montant.motif });
    }

    const arret = jours(r.joursArret);
    if (arret.motif === "vide") manques.push("joursArret");
    else if (arret.motif) invalides.push({ champ: "joursArret", motif: arret.motif });

    if (mere && !arret.motif && arret.valeur < ARRET_MINIMAL_JOURS) {
        alertes.push("arretTropCourt");
    }
    if (!mere && !arret.motif) {
        const plafond = r.naissances === "multiple" ? PATERNITE_JOURS.multiple : PATERNITE_JOURS.simple;
        if (arret.valeur > plafond) alertes.push("audelaDuPlafond");
    }
    if (auPlancher(r)) alertes.push("revenuSousLePlancher");

    return {
        valide: manques.length === 0 && invalides.length === 0,
        manques: [...new Set(manques)],
        invalides,
        alertes,
    };
}

/** Ce que le parcours annonce, ligne par ligne. */
export function recapitulatif(reponses) {
    const r = reponses || {};
    if (!valider(r).valide) return null;
    const mere = r.parent === "mere";
    const droits = mere ? droitsMaternite(r) : droitsPaternite(r);
    if (!droits) return null;

    if (mere) {
        const c = droits.calendrier;
        return [
            ["Congé indemnisable", `${c.total} jours, du ${dateEnToutesLettres(c.debut)} au ${dateEnToutesLettres(c.fin)}`],
            [
                "Période obligatoire",
                `${ARRET_MINIMAL_JOURS} jours, du ${dateEnToutesLettres(c.obligatoireDebut)} au ${dateEnToutesLettres(c.obligatoireFin)}`,
            ],
            ["Allocation de repos maternel", `${euros(droits.allocation)}, en deux versements de ${euros(droits.versement)}`],
            [
                "Indemnités journalières",
                droits.assezLong
                    ? `${euros(droits.ij)}, soit ${euros(droits.journaliere)} sur ${droits.joursIndemnises} jours`
                    : "aucune : l'arrêt envisagé est plus court que huit semaines",
            ],
            ["Total", euros(droits.total)],
        ];
    }

    return [
        ["Congé indemnisable", `${droits.calendrier.plafond} jours au plus`],
        ["À prendre avant le", dateEnToutesLettres(droits.calendrier.limite)],
        ["Indemnités journalières", `${euros(droits.ij)}, soit ${euros(droits.journaliere)} sur ${droits.joursIndemnises} jours`],
        ["Allocation forfaitaire", "aucune : elle n'existe qu'en maternité"],
        ["Total", euros(droits.total)],
    ];
}
